import React, { useState } from 'react';
import { 
  FaBold, 
  FaItalic, 
  FaListUl, 
  FaListOl, 
  FaLink, 
  FaCode, 
  FaQuoteRight,
  FaClipboard,
} from 'react-icons/fa';
import './MarkdownAssistant.css';

const MarkdownAssistant = ({ onInsert }) => {
  const [openDropdownIndex, setOpenDropdownIndex] = useState(null);
  const [openCodeDropdown, setOpenCodeDropdown] = useState(false);

  const toggleDropdown = (index, e) => {
    e.preventDefault(); // Prevent form submission
    setOpenDropdownIndex(openDropdownIndex === index ? null : index);
  };

  const toggleCodeDropdown = (e) => {
    e.preventDefault();
    setOpenCodeDropdown(!openCodeDropdown);
  };

  const handleInsert = (snippet, e) => {
    e.preventDefault(); 
    onInsert(snippet);
    setOpenDropdownIndex(null);
    setOpenCodeDropdown(false);
  };

  const codeSnippets = [
    { text: 'Bash', snippet: '```bash\n// Your code here\n```' },
    { text: 'JavaScript', snippet: '```javascript\n// Your code here\n```' },
    { text: 'Python', snippet: '```python\n# Your code here\n```' },
    { text: 'C++', snippet: '```c++\n// Your code here\n```' },
    { text: 'HTML', snippet: '```html\n<!-- Your code here -->\n```' },
    { text: 'Java', snippet: '```java\n// Your code here\n```' },
    { text: 'Ruby', snippet: '```ruby\n# Your code here\n```' },
    { text: 'Go', snippet: '```go\n// Your code here\n```' },
    { text: 'TypeScript', snippet: '```typescript\n// Your code here\n```' },
  ];

  const tools = [
    // Math dropdown group
    { icon: <FaCode />, text: 'Code', description: 'Add code block', dropdown: codeSnippets },
    {
      text: 'Math',
      description: 'Insert math elements',
      dropdown: [
        { text: 'Inline Math: $...$', snippet: '$...$', description: 'Insert inline math' },
        { text: 'Display Math: $$...$$', snippet: '$$\n\n$$', description: 'Insert display math' },
        { text: 'Fraction: \\frac{}{}', snippet: '$\\frac{ }{ }$', description: 'Insert fraction' },
        { text: 'Integral: \\int', snippet: '$\\int$', description: 'Insert integral' },
        { text: 'Summation: \\sum', snippet: '$\\sum$', description: 'Insert summation' },
        { text: 'Limit: \\lim_{n\\to\\infty}', snippet: '$\\lim_{n\\to\\infty}$', description: 'Insert limit' },
        { text: 'Square Root: \\sqrt{}', snippet: '$\\sqrt{ }$', description: 'Insert square root' },
        { text: 'Derivative: \\frac{d}{dx}', snippet: '$\\frac{d}{dx}$', description: 'Insert derivative' },
        { text: 'Sine: \\sin', snippet: '$\\sin$', description: 'Insert sine function' },
        { text: 'Cosine: \\cos', snippet: '$\\cos$', description: 'Insert cosine function' },
        { text: 'Tangent: \\tan', snippet: '$\\tan$', description: 'Insert tangent function' }
      ]
    },
    // Table dropdown group
    {
      text: 'Table',
      description: 'Insert table elements',
      dropdown: [
        { text: 'Simple Table', snippet: '| Header1 | Header2 |\n| --- | --- |\n| Cell1 | Cell2 |', description: 'Insert a simple table' },
        { text: 'Advanced Table', snippet: '| Header1 | Header2 | Header3 |\n| --- | --- | --- |\n| Cell1 | Cell2 | Cell3 |\n| Cell4 | Cell5 | Cell6 |', description: 'Insert an advanced table' },
        { text: 'Table with Alignment', snippet: '| Left | Center | Right |\n| :--- | :---: | ---: |\n| Text | Text | Text |', description: 'Insert a table with alignment' }
      ]
    },
    // Header dropdown group
    {
      text: 'Header',
      description: 'Insert header elements',
      dropdown: [
        { text: 'H1: # Header', snippet: '# ', description: 'Insert H1 header' },
        { text: 'H2: ## Header', snippet: '## ', description: 'Insert H2 header' },
        { text: 'H3: ### Header', snippet: '### ', description: 'Insert H3 header' },
        { text: 'H4: #### Header', snippet: '#### ', description: 'Insert H4 header' }
      ]
    },
    { icon: <FaBold />, text: '**Bold**', description: 'Make text bold (Ctrl+B)' },
    { icon: <FaItalic />, text: '*Italic*', description: 'Make text italic (Ctrl+I)' },
    { icon: <FaListUl />, text: '- Bullet', description: 'Add bullet list' },
    { icon: <FaListOl />, text: '1. Numbered', description: 'Add numbered list' },
    { icon: <FaLink />, text: '[Text](url)', description: 'Add link (Ctrl+K)' },
    { icon: <FaQuoteRight />, text: '> Quote', description: 'Add blockquote' }
  ];

  return (
    <div className="markdown-assistant">
      {tools.map((tool, index) => (
        <div key={index} className="dropdown">
          {tool.dropdown ? (
            <>
              <button
                type="button"
                className="tool-btn"
                onClick={(e) => toggleDropdown(index, e)}
                title={tool.description}
              >
                {tool.icon} {tool.text}
              </button>
              {openDropdownIndex === index && (
                <div className="dropdown-menu">
                  {tool.dropdown.map((item, subIndex) => (
                    <button
                      type="button"
                      key={subIndex}
                      className="dropdown-item"
                      onClick={(e) => handleInsert(item.snippet, e)}
                      title={item.description}
                    >
                      {item.text}
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <button
              type="button"
              className="tool-btn"
              onClick={(e) => handleInsert(tool.text, e)}
              title={tool.description}
            >
              {tool.icon}
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export default MarkdownAssistant;